import {computed, effect, Injectable, PLATFORM_ID, signal, inject} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {catchError, finalize, map, Observable, of, tap} from 'rxjs';
import {environment} from '../../environments/environment';
import {CurrentUser} from '../models/api/CurrentUser';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly currentUserUrl = `${environment.apiUrl}/auth/profile`;
  private static readonly logoutUrl = `${environment.apiUrl}/auth/logout`;
  private static readonly heartbeatIntervalMs = 120000;
  private static readonly minimumRevalidationGapMs = 15000;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT, {optional: true});

  private readonly _currentUser = signal<CurrentUser | null>(null);
  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() != null);
  private readonly _currentUserResolved = signal(false);
  readonly currentUserResolved = this._currentUserResolved.asReadonly();
  private readonly _currentUserLoading = signal(false);
  readonly currentUserLoading = this._currentUserLoading.asReadonly();
  private readonly _sessionExpired = signal(false);
  readonly sessionExpired = this._sessionExpired.asReadonly();
  readonly shouldShowProtectedContent = computed(() => this.isAuthenticated() || this.sessionExpired());
  private readonly documentVisible = signal(true);

  private heartbeatId: ReturnType<typeof globalThis.setInterval> | null = null;
  private monitoringInitialized = false;
  private lastRevalidationAt = 0;

  constructor(private http: HttpClient) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.documentVisible.set(this.document?.visibilityState !== 'hidden');
    this.initializeSessionMonitoring();

    effect(() => {
      if (this.isAuthenticated() && !this.sessionExpired() && this.documentVisible()) {
        this.startHeartbeat();
      } else {
        this.stopHeartbeat();
      }
    });
  }

  initializeCurrentUser(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this._currentUser.set(null);
      this._currentUserLoading.set(false);
      this._currentUserResolved.set(true);
      return;
    }

    if (this._currentUserResolved() || this._currentUserLoading()) {
      return;
    }

    this.loadCurrentUser().subscribe();
  }

  loadCurrentUser(options: {preserveOnError?: boolean} = {}): Observable<CurrentUser | null> {
    const preserveOnError = options.preserveOnError ?? false;
    if (!isPlatformBrowser(this.platformId)) {
      this._currentUser.set(null);
      this._currentUserResolved.set(true);
      this._currentUserLoading.set(false);
      this._sessionExpired.set(false);
      return of(null);
    }

    this._currentUserLoading.set(true);
    return this.http.get<CurrentUser>(AuthService.currentUserUrl).pipe(
      tap((user) => {
        this._currentUser.set(user);
        this._sessionExpired.set(false);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.handleSessionExpired();
        } else if (!preserveOnError) {
          this._currentUser.set(null);
          this._sessionExpired.set(false);
        }
        return of(null);
      }),
      finalize(() => {
        this._currentUserLoading.set(false);
        this._currentUserResolved.set(true);
      })
    );
  }

  login(provider: 'google' | 'office365' = 'google'): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this._sessionExpired.set(false);
    window.location.href = `${environment.backendUrl}/oauth2/authorization/${provider}`;
  }

  logout(): Observable<boolean> {
    return this.http.post(AuthService.logoutUrl, {}, {responseType: 'text'}).pipe(
      tap(() => this.resetSessionState()),
      map(() => true),
      catchError(() => of(false))
    );
  }

  handleSessionExpired(): void {
    if (this._currentUser() == null && !this._sessionExpired()) {
      return;
    }

    this._currentUser.set(null);
    this._currentUserLoading.set(false);
    this._currentUserResolved.set(true);
    this._sessionExpired.set(true);
    this.stopHeartbeat();
  }

  revalidateSession(force = false): void {
    if (!isPlatformBrowser(this.platformId) || !this.isAuthenticated() || this._currentUserLoading()) {
      return;
    }

    if (!force && !this.documentVisible()) {
      return;
    }

    const now = Date.now();
    if (now - this.lastRevalidationAt < AuthService.minimumRevalidationGapMs) {
      return;
    }
    this.lastRevalidationAt = now;
    this.loadCurrentUser({preserveOnError: true}).subscribe();
  }

  private resetSessionState(): void {
    this._currentUser.set(null);
    this._currentUserLoading.set(false);
    this._currentUserResolved.set(true);
    this._sessionExpired.set(false);
    this.stopHeartbeat();
  }

  private initializeSessionMonitoring(): void {
    if (this.monitoringInitialized || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.monitoringInitialized = true;

    globalThis.addEventListener('focus', () => this.revalidateSession(true));
    this.document?.addEventListener('visibilitychange', () => {
      const visible = this.document?.visibilityState !== 'hidden';
      this.documentVisible.set(visible);
      if (visible) {
        this.revalidateSession(true);
      }
    });
  }

  private startHeartbeat(): void {
    if (this.heartbeatId != null) {
      return;
    }
    this.heartbeatId = globalThis.setInterval(() => this.revalidateSession(), AuthService.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatId == null) {
      return;
    }
    globalThis.clearInterval(this.heartbeatId);
    this.heartbeatId = null;
  }
}
